import styled from 'styled-components';
import {CSSProperties, ReactNode, memo} from 'react';
import {Avatar} from '../../../atoms/avatar';
import {HorizontalSpacer} from '../../../atoms/spacer';
import {Body2} from '../../../atoms/typography/body2';
import {Body1} from '../../../atoms/typography/body1';
import {Tooltip} from '../../tooltip';
import {getColorFromTheme} from '../../../ui-utils';

export interface UserListCardProps {
  type: 'primary' | 'secondary' | 'aligned';
  name: string;
  avatar?: string;
  kind?: string;
  avatarName?: any;
  reviewer?: any;
  users?: any;
  userId: string;
  optional?: string;
  avatarIcon?: ReactNode;
  textStyle?: CSSProperties;
  tooltip: boolean;
  TextComponent?: any;
  avatarSize?: 'sm' | 'md' | 'lg' | 'xs';
  textKind?: string;
  tooltipText?: boolean;
  secondaryText?: string;
  badgeText?: string;
  timezone?: string;
  showTimezone?: boolean;
}

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: auto 8px 1fr;
  align-items: center;
  min-width: 0;
`;

const IdentityText = styled.div`
  min-width: 0;

  p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const IdentityMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const IdentityBadge = styled.span`
  flex: 0 0 auto;
  padding: 2px 8px;
  border-radius: 8px;
  background: ${getColorFromTheme('backgroundDark')};
  color: ${getColorFromTheme('textDark')};
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
`;

export const UserListCard = memo(
  ({
    type,
    avatar,
    name,
    kind,
    avatarName,
    users,
    userId,
    tooltip,
    TextComponent,
    avatarIcon,
    textKind,
    textStyle,
    avatarSize,
    optional,
    tooltipText,
    secondaryText,
    badgeText,
    timezone,
    showTimezone,
  }: UserListCardProps) => {
    const TextComponentControllerPrimary = TextComponent
      ? TextComponent
      : Body1;
    const TextComponentControllerAligned = TextComponent
      ? TextComponent
      : Body1;
    const TextComponentControllerSecondary = TextComponent
      ? TextComponent
      : Body2;

    switch (type) {
      case 'primary':
        return (
          <Wrapper>
            <Avatar
              tooltip={tooltip}
              size="md"
              src={avatar}
              name={name}
              avatarIcon={avatarIcon}
              alt={name}
              userId={userId}
              timezone={timezone}
              showTimezone={showTimezone}
            />
            <HorizontalSpacer size="8px" />
            <TextComponentControllerPrimary
              style={textStyle}
              kind={
                kind === 'admin' && !textKind
                  ? 'textDark'
                  : textKind
                  ? textKind
                  : 'textBody'
              }>
              {name}
            </TextComponentControllerPrimary>
          </Wrapper>
        );
      case 'aligned':
        return (
          <Wrapper>
            <Avatar
              tooltip={tooltip}
              size="sm"
              src={avatar}
              name={name}
              alt={name}
              userId={userId}
              timezone={timezone}
              showTimezone={showTimezone}
            />
            <HorizontalSpacer size="8px" />

            {tooltipText ? (
              <Tooltip
                text={name}
                tooltipBody={name}
                maxLength={13}
                BodyTextNodeType={TextComponentControllerAligned}
                bodyTextNodeKind={
                  kind === 'admin' && !textKind
                    ? 'textDark'
                    : textKind
                    ? textKind
                    : 'textBody'
                }
                bodyStyle={{fontWeight: 600, fontSize: '10px'}}
                noCapitalize
                withEllipsis
              />
            ) : (
              <TextComponentControllerAligned
                kind={
                  kind === 'admin' && !textKind
                    ? 'textDark'
                    : textKind
                    ? textKind
                    : 'textBody'
                }
                style={textStyle || {fontWeight: 600, fontSize: '10px'}}>
                {name}
              </TextComponentControllerAligned>
            )}
          </Wrapper>
        );

      case 'secondary':
        return (
          <Wrapper>
            <Avatar
              tooltip={tooltip}
              src={avatar}
              name={avatarName || name}
              optional={optional}
              customUsers={users}
              size={avatarSize}
              alt={avatarName || name}
              avatarIcon={avatarIcon}
              userId={userId}
              timezone={timezone}
              showTimezone={showTimezone}
            />
            <HorizontalSpacer size="8px" />

            {tooltipText ? (
              <Tooltip
                text={name}
                tooltipBody={name}
                maxLength={13}
                BodyTextNodeType={TextComponentControllerSecondary}
                bodyTextNodeKind={textKind ? textKind : 'textDark'}
                noCapitalize
                withEllipsis
              />
            ) : (
              <IdentityText>
                <TextComponentControllerSecondary
                  style={textStyle}
                  kind={textKind ? textKind : 'textDark'}>
                  {name}
                </TextComponentControllerSecondary>
                {(secondaryText || badgeText) && (
                  <IdentityMeta>
                    {secondaryText && (
                      <Body2 kind="textBody">{secondaryText}</Body2>
                    )}
                    {badgeText && <IdentityBadge>{badgeText}</IdentityBadge>}
                  </IdentityMeta>
                )}
              </IdentityText>
            )}
          </Wrapper>
        );
      default:
        throw new Error(
          'Invalid list card type, you must specify primary|secondary',
        );
    }
  },
);

UserListCard.displayName = 'UserListCard';
